import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { formatARS } from '../lib/format';
import { errorCopy } from '../lib/errors';
import { addDays, todayLocal, toLocalDay } from '../lib/datetime';

interface Service { id: string; name: string; price_cents: number; duration_min: number; }
interface Barber { id: string; name: string; }
interface SalonInfo {
  salon: { slug: string; name: string; phone: string | null; address: string | null; city: string | null };
  services: Service[];
  barbers: Barber[];
  hours: { weekday: number; open_time: string | null; close_time: string | null; closed: number }[];
}

type Step = 'service' | 'barber' | 'date' | 'time' | 'client' | 'done';

export default function ReservaPublicaPage() {
  const { slug } = useParams<{ slug: string }>();
  const [info, setInfo] = useState<SalonInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>('service');
  const [serviceId, setServiceId] = useState('');
  const [barberId, setBarberId] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [slots, setSlots] = useState<string[] | null>(null);
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ when: string } | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const res = await api<SalonInfo>(`/api/r/${slug}/info`);
      if (res.ok) setInfo(res.data);
      else setLoadError(errorCopy(res.error));
    })();
  }, [slug]);

  useEffect(() => {
    if (step !== 'time' || !serviceId || !barberId) return;
    setSlots(null);
    (async () => {
      const res = await api<{ slots: string[] }>(`/api/r/${slug}/availability?date=${date}&service_id=${serviceId}&barber_id=${barberId}`);
      if (res.ok) setSlots(res.data.slots);
      else setSlots([]);
    })();
  }, [step, slug, serviceId, barberId, date]);

  async function book() {
    if (!serviceId || !barberId || !time || !name || !phone) return;
    setError(null); setSubmitting(true);
    const [h, m] = time.split(':').map(Number);
    const [y, mo, d] = date.split('-').map(Number);
    const startsAt = new Date(Date.UTC(y!, mo! - 1, d!, (h ?? 0) + 3, m ?? 0, 0)).toISOString();
    const res = await api<{ appointment: { starts_at: string } }>(`/api/r/${slug}/book`, {
      method: 'POST',
      body: { service_id: serviceId, barber_id: barberId, starts_at: startsAt, client: { name, phone } },
    });
    setSubmitting(false);
    if (!res.ok) {
      if (res.error === 'slot_taken') setError('Justo te lo ganaron. Probá otro horario.');
      else setError(errorCopy(res.error));
      return;
    }
    setSuccess({ when: res.data.appointment.starts_at });
    setStep('done');
  }

  if (loadError) return <Centered>{loadError}</Centered>;
  if (!info) return <Centered>Bancá un toque…</Centered>;

  const next14days = Array.from({ length: 14 }, (_, i) => addDays(todayLocal(), i));
  const selectedService = info.services.find((s) => s.id === serviceId);
  const selectedBarber = info.barbers.find((b) => b.id === barberId);

  if (step === 'done' && success) {
    const when = new Date(success.when);
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Logo size={48} className="text-ink mb-6" />
        <h1 className="text-4xl font-semibold tracking-tighter mb-3">Listo, te esperamos.</h1>
        <p className="text-lg text-ink/70 mb-2">{when.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long', day: 'numeric', month: 'long' })}</p>
        <p className="num text-2xl text-forest font-medium">{when.toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false })}</p>
        <p className="mt-6 text-sm text-ash">Te vamos a mandar un WhatsApp un día antes para recordarte.</p>
        <div className="mt-8 text-ink/60 text-sm">{info.salon.name}{info.salon.address ? ` · ${info.salon.address}` : ''}</div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center px-6 py-10 bg-bone">
      <header className="text-center mb-8">
        <Logo size={28} className="text-ink mx-auto mb-3" />
        <h1 className="text-2xl font-semibold tracking-tighter">{info.salon.name}</h1>
        {info.salon.address && <p className="text-sm text-ash">{info.salon.address}{info.salon.city ? `, ${info.salon.city}` : ''}</p>}
      </header>

      <div className="w-full max-w-md">
        {step === 'service' && (
          <Card title="¿Qué te vas a hacer?">
            <ul className="space-y-2">
              {info.services.map((s) => (
                <li key={s.id}>
                  <button onClick={() => { setServiceId(s.id); setStep('barber'); }} className="w-full text-left p-4 rounded-xl border border-ink/10 hover:border-ink bg-white transition flex justify-between items-center">
                    <div><div className="font-medium">{s.name}</div><div className="text-sm text-ash">{s.duration_min} min</div></div>
                    <div className="num font-medium">{formatARS(s.price_cents)}</div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {step === 'barber' && (
          <Card title="¿Con quién?" back={() => setStep('service')}>
            <ul className="space-y-2">
              {info.barbers.map((b) => (
                <li key={b.id}>
                  <button onClick={() => { setBarberId(b.id); setStep('date'); }} className="w-full p-4 rounded-xl border border-ink/10 hover:border-ink bg-white transition text-left font-medium">
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {step === 'date' && (
          <Card title="¿Qué día?" back={() => setStep('barber')}>
            <div className="grid grid-cols-3 gap-2">
              {next14days.map((d) => (
                <button key={d} onClick={() => { setDate(d); setStep('time'); }} className="p-3 rounded-xl border border-ink/10 hover:border-ink bg-white transition text-center text-sm">
                  {toLocalDay(new Date(`${d}T03:00:00.000Z`))}
                </button>
              ))}
            </div>
          </Card>
        )}

        {step === 'time' && (
          <Card title="¿A qué hora?" back={() => setStep('date')}>
            {slots === null ? <p className="text-ash text-center py-8">Bancá un toque…</p> :
             slots.length === 0 ? <p className="text-ash text-center py-8">Ese día no hay lugar. Probá otro.</p> :
             <div className="grid grid-cols-3 gap-2">
               {slots.map((s) => (
                 <button key={s} onClick={() => { setTime(s); setStep('client'); }} className="num p-3 rounded-xl border border-ink/10 hover:border-ink bg-white transition text-center font-medium">
                   {s}
                 </button>
               ))}
             </div>}
          </Card>
        )}

        {step === 'client' && (
          <Card title="Tus datos" back={() => setStep('time')}>
            <div className="text-sm text-ink/70 mb-4 p-3 bg-white border border-ink/10 rounded-lg">
              {selectedService?.name} · {selectedBarber?.name} · <span className="num">{date} {time}</span>
            </div>
            <div className="space-y-3">
              <input className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none" placeholder="Tu nombre" value={name} onChange={(e) => setName(e.target.value)} />
              <input className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none" placeholder="Tu WhatsApp (ej. 11 1234 5678)" value={phone} onChange={(e) => setPhone(e.target.value)} />
              {error && <div className="text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-4 py-3">{error}</div>}
              <button onClick={book} disabled={submitting} className="btn-primary w-full disabled:opacity-50">{submitting ? 'Reservando…' : 'Confirmar mi turno'}</button>
              <p className="text-xs text-ash text-center">Te avisamos por WhatsApp un día antes.</p>
            </div>
          </Card>
        )}
      </div>
    </main>
  );
}

function Card({ title, children, back }: { title: string; children: React.ReactNode; back?: () => void }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        {back && <button onClick={back} className="text-ink/60 hover:text-ink">←</button>}
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="min-h-screen flex items-center justify-center text-ash px-6">{children}</main>;
}
